/**
 * "필요해요" 수요 체크 — 품목 사전 · 도시 선택 목록.
 *
 * 블로그 본문 마커 `<div data-demand-item="pocket-wifi" data-demand-city="JP-TYO"></div>`
 * 의 품목 키를 화면 이름으로 바꾼다. 새 품목은 여기 한 줄 추가하면 된다(키는 DB
 * 제약과 같은 kebab-case, 40자 이하).
 */

import { CITIES, COUNTRIES, getCityById, type City } from "@/config/locations";
import { locales, type Locale } from "@/i18n/routing";

export type DemandLocale = Locale;

export const DEMAND_LOCALES: readonly DemandLocale[] = locales;

export function toDemandLocale(locale: string): DemandLocale {
  return (DEMAND_LOCALES as readonly string[]).includes(locale)
    ? (locale as DemandLocale)
    : "en";
}

export const DEMAND_ITEMS: Record<string, Record<DemandLocale, string>> = {
  "pocket-wifi": {
    ko: "포켓와이파이",
    en: "Pocket Wi-Fi",
    ja: "ポケットWi-Fi",
    "zh-CN": "随身Wi-Fi",
    "zh-TW": "隨身Wi-Fi",
  },
  "power-bank": {
    ko: "보조배터리",
    en: "Power bank",
    ja: "モバイルバッテリー",
    "zh-CN": "充电宝",
    "zh-TW": "行動電源",
  },
  wheelchair: {
    ko: "휠체어",
    en: "Wheelchair",
    ja: "車いす",
    "zh-CN": "轮椅",
    "zh-TW": "輪椅",
  },
  luggage: {
    ko: "캐리어",
    en: "Suitcase",
    ja: "スーツケース",
    "zh-CN": "行李箱",
    "zh-TW": "行李箱",
  },
};

export const DEMAND_ITEM_KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** 사전에 있는 품목 키인가 — 모르는 키의 마커는 렌더하지 않는다. */
export function isDemandItemKey(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(DEMAND_ITEMS, key);
}

export function demandItemName(key: string, locale: string): string {
  const names = DEMAND_ITEMS[key];
  if (!names) return key;
  return names[toDemandLocale(locale)] ?? names.en;
}

/** CITIES 에 있는 id 만 통과(대소문자 무시). 없으면 null. */
export function normalizeDemandCityId(cityId: string | null | undefined): string | null {
  if (!cityId) return null;
  const city = getCityById(cityId.trim().toUpperCase());
  return city ? city.id : null;
}

export function demandCityLabel(cityId: string, locale: string): string {
  const city = getCityById(cityId);
  if (!city) return cityId;
  return city.name[toDemandLocale(locale)] || city.name.en;
}

export interface DemandCityGroup {
  countryCode: string;
  countryLabel: string;
  cities: { id: string; label: string }[];
}

/** 서비스 국가(한국·일본)를 앞에 — 나머지는 COUNTRIES 순서. */
const SERVICE_COUNTRY_ORDER = ["KR", "JP"];

/**
 * 도시 선택 목록 — 나라별 묶음(optgroup), 서비스 국가 먼저, 나라 안은 priority 순.
 * 좌표가 없는 도시는 검색 대상이 아니므로 뺀다.
 */
export function getDemandCityGroups(locale: string): DemandCityGroup[] {
  const lang = toDemandLocale(locale);
  const byCountry = new Map<string, City[]>();
  for (const city of CITIES) {
    if (!city.coordinates) continue;
    const list = byCountry.get(city.countryCode) ?? [];
    list.push(city);
    byCountry.set(city.countryCode, list);
  }

  const countryOrder = [
    ...SERVICE_COUNTRY_ORDER,
    ...COUNTRIES.map((c) => c.code).filter((code) => !SERVICE_COUNTRY_ORDER.includes(code)),
  ];

  const groups: DemandCityGroup[] = [];
  for (const code of countryOrder) {
    const cities = byCountry.get(code);
    if (!cities || cities.length === 0) continue;
    const country = COUNTRIES.find((c) => c.code === code);
    groups.push({
      countryCode: code,
      countryLabel: country ? country.name[lang] || country.name.en : code,
      cities: [...cities]
        .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))
        .map((c) => ({ id: c.id, label: c.name[lang] || c.name.en })),
    });
  }
  return groups;
}
